import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Cog6ToothIcon } from '@heroicons/react/24/outline';
import { api } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { AgentDesktopSetupDrawer } from '../../../components/admin/AgentDesktopSetup';
import { VpsAgentPicker, VpsPageHeader, fmt, removeVpsAgent, useVpsAgents } from './vpsShared';

export default function VpsOverview() {
  const { user } = useAuth();
  const { agents, selectedId, selectAgent, loading: agentsLoading, reload: reloadAgents } = useVpsAgents();
  const [lifetime, setLifetime] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [configureAgent, setConfigureAgent] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setStatsLoading(true);
      try {
        const statsRes = await api.get('/admin/va/ai-agents/stats/lifetime', {
          params: { period: 'today' }
        });
        if (cancelled) return;
        setLifetime(statsRes.data || null);
      } catch (err) {
        console.error(err);
      } finally {
        if (!cancelled) setStatsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Keep drawer agent in sync when the shared list refreshes.
  useEffect(() => {
    if (!configureAgent?._id) return;
    const next = agents.find((a) => String(a._id) === String(configureAgent._id));
    if (next) setConfigureAgent(next);
  }, [agents, configureAgent?._id]);

  if (user?.role !== 'admin') {
    return <div className="text-sm text-gray-500">Admin access required.</div>;
  }

  const loading = (agentsLoading && !agents.length) || statsLoading;
  const online = agents.filter((a) => {
    const t = a.runtime?.lastSeenAt || a.liveHeartbeat?.at;
    return t && Date.now() - new Date(t).getTime() < 90_000;
  }).length;
  const running = agents.filter((a) => a.status === 'running' || a.status === 'pausing').length;

  const summary = lifetime?.summary || lifetime?.totals || {};
  const follows = summary.follows ?? summary.totalFollowed ?? lifetime?.agents?.reduce?.(
    (s, a) => s + (a.period?.follows || a.totalFollowed || 0),
    0
  );
  const followBacks = summary.followBacks ?? summary.totalFollowBacks;

  return (
    <div className="space-y-6">
      <VpsPageHeader
        title="VPS Overview"
        subtitle="Desktop follow fleet — same AI VA agents, focused ops surface."
        actions={(
          <Link to="/dashboard/admin/vps/automations" className="btn-primary !py-2 !px-4 text-sm">
            Open automations
          </Link>
        )}
      />

      {loading ? (
        <div className="text-sm text-gray-500">Loading…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { label: 'Agents', value: agents.length },
              { label: 'Desktop online', value: online },
              { label: 'Running now', value: running },
              { label: 'Follows today', value: follows }
            ].map((c) => (
              <div
                key={c.label}
                className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-4"
              >
                <div className="text-xs uppercase tracking-wide text-gray-500">{c.label}</div>
                <div className="mt-1 text-2xl font-semibold tabular-nums">{fmt(c.value)}</div>
              </div>
            ))}
          </div>

          {followBacks != null && (
            <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-5 py-4 text-sm text-gray-600 dark:text-gray-300">
              Follow-backs today: <span className="font-semibold tabular-nums text-gray-900 dark:text-white">{fmt(followBacks)}</span>
            </div>
          )}

          <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-800 flex flex-wrap items-center justify-between gap-2">
              <div className="text-sm font-semibold">Agents</div>
              <VpsAgentPicker
                agents={agents}
                selectedId={selectedId}
                onSelect={selectAgent}
                onChanged={async (id) => {
                  await reloadAgents({ force: true, silent: true });
                  if (id) selectAgent(id);
                }}
              />
            </div>
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800/50 text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-4 py-2 text-left font-medium">Name</th>
                  <th className="px-4 py-2 text-left font-medium">Status</th>
                  <th className="px-4 py-2 text-left font-medium">Accounts</th>
                  <th className="px-4 py-2 text-left font-medium">Desktop</th>
                  <th className="px-4 py-2 text-right font-medium">Setup</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {agents.map((a) => {
                  const seen = a.runtime?.lastSeenAt || a.liveHeartbeat?.at;
                  const isOnline = seen && Date.now() - new Date(seen).getTime() < 90_000;
                  return (
                    <tr key={a._id}>
                      <td className="px-4 py-2.5 font-medium">
                        <Link
                          className="text-indigo-600 dark:text-indigo-400 hover:underline"
                          to={`/dashboard/admin/vps/automations`}
                          onClick={() => {
                            try { localStorage.setItem('wingr.vps.selectedAgentId', String(a._id)); } catch { /* */ }
                          }}
                        >
                          {a.name}
                        </Link>
                      </td>
                      <td className="px-4 py-2.5">{a.status || 'idle'}</td>
                      <td className="px-4 py-2.5 tabular-nums">
                        {fmt(a.enabledAccountCount ?? a.accountCount)}
                      </td>
                      <td className="px-4 py-2.5">
                        <span className={isOnline ? 'text-emerald-600' : 'text-gray-400'}>
                          {isOnline ? 'online' : 'offline'}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-right space-x-3">
                        <button
                          type="button"
                          onClick={() => setConfigureAgent(a)}
                          className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:underline"
                        >
                          <Cog6ToothIcon className="w-3.5 h-3.5" />
                          Configure
                        </button>
                        <button
                          type="button"
                          onClick={() => removeVpsAgent(a, async () => {
                            await reloadAgents({ force: true, silent: true });
                          })}
                          className="text-xs font-medium text-red-600 dark:text-red-400 hover:underline"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {!agents.length && (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                      No agents yet. Use New VPS above to add one, then pair the desktop worker with the token.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      <AgentDesktopSetupDrawer
        agent={configureAgent}
        open={Boolean(configureAgent)}
        onClose={() => setConfigureAgent(null)}
        onAgentUpdated={() => reloadAgents({ silent: true, force: true })}
      />
    </div>
  );
}
